// 六边形工具函数
// 使用平顶六边形（flat-top）+ odd-q offset 坐标

import type { Cell } from '../types.js';

export const HEX_ANGLES: number[] = [0, Math.PI / 3, Math.PI * 2 / 3, Math.PI, Math.PI * 4 / 3, Math.PI * 5 / 3];

// 单元格坐标转像素坐标
// 公式来自设计文档
export function cellToPixel(col: number, row: number, hexSize: number, cropLeft: number, cropTop: number): { x: number; y: number } {
  const width = hexSize * Math.sqrt(3);
  const x = cropLeft + col * (hexSize * 1.5) + hexSize;
  let y: number;
  if (col % 2 === 0) {
    // 偶数列
    y = cropTop + row * (hexSize * Math.sqrt(3)) + (hexSize * Math.sqrt(3)) / 2;
  } else {
    // 奇数列，向下偏移半个六边形高度
    y = cropTop + row * (hexSize * Math.sqrt(3)) + (hexSize * Math.sqrt(3));
  }
  return { x, y };
}

// 六边形6个顶点坐标
export function getHexVertices(cx: number, cy: number, size: number): { x: number; y: number }[] {
  const vertices: { x: number; y: number }[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = Math.PI / 3 * i;
    vertices.push({
      x: cx + size * Math.cos(angle),
      y: cy + size * Math.sin(angle),
    });
  }
  return vertices;
}

// 绘制六边形路径
export function drawHexPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = Math.PI / 3 * i;
    const vx = cx + size * Math.cos(angle);
    const vy = cy + size * Math.sin(angle);
    if (i === 0) ctx.moveTo(vx, vy);
    else ctx.lineTo(vx, vy);
  }
  ctx.closePath();
}

// 获取邻居坐标
export function getNeighbors(col: number, row: number): Cell[] {
  if (col % 2 === 0) {
    return [
      { col: col, row: row - 1 },
      { col: col, row: row + 1 },
      { col: col - 1, row: row - 1 },
      { col: col + 1, row: row - 1 },
      { col: col - 1, row: row },
      { col: col + 1, row: row },
    ];
  } else {
    return [
      { col: col, row: row - 1 },
      { col: col, row: row + 1 },
      { col: col - 1, row: row },
      { col: col + 1, row: row },
      { col: col - 1, row: row + 1 },
      { col: col + 1, row: row + 1 },
    ];
  }
}

// 判断两个Cell是否相等
export function cellEq(a: Cell, b: Cell): boolean {
  return a.col === b.col && a.row === b.row;
}

// 判断cell是否在cells数组中
export function cellInList(cell: Cell, cells: Cell[]): boolean {
  return cells.some(c => cellEq(c, cell));
}

// 计算碎片的边界框
export function computeBoundingBox(cells: Cell[]): { minCol: number; maxCol: number; minRow: number; maxRow: number } {
  let minCol = Infinity, maxCol = -Infinity, minRow = Infinity, maxRow = -Infinity;
  for (const c of cells) {
    minCol = Math.min(minCol, c.col);
    maxCol = Math.max(maxCol, c.col);
    minRow = Math.min(minRow, c.row);
    maxRow = Math.max(maxRow, c.row);
  }
  return { minCol, maxCol, minRow, maxRow };
}

// 判断一个cell是否在网格边界上
export function isEdgeCell(col: number, row: number, cols: number, rows: number): boolean {
  if (col < 0 || col >= cols || row < 0 || row >= rows) return false;
  return col === 0 || col === cols - 1 || row === 0 || row === rows - 1;
}

// 计算碎片占据的所有cells的邻居（用于连锁解锁）
export function getPieceNeighbors(cells: Cell[]): Cell[] {
  const neighbors: Cell[] = [];
  for (const cell of cells) {
    for (const n of getNeighbors(cell.col, cell.row)) {
      if (!cellInList(n, cells) && !cellInList(n, neighbors)) {
        neighbors.push(n);
      }
    }
  }
  return neighbors;
}

// 从一组cells计算像素边界框（用于图片切割）
export function getPixelBoundingBox(
  cells: Cell[],
  hexSize: number,
  cropLeft: number,
  cropTop: number
): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const cell of cells) {
    const { x, y } = cellToPixel(cell.col, cell.row, hexSize, cropLeft, cropTop);
    const vertices = getHexVertices(x, y, hexSize);
    for (const v of vertices) {
      minX = Math.min(minX, v.x);
      minY = Math.min(minY, v.y);
      maxX = Math.max(maxX, v.x);
      maxY = Math.max(maxY, v.y);
    }
  }
  return { minX, minY, maxX, maxY };
}

// 随机打乱数组
export function shuffle<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// 把碎片的cells归一化（减去offset），得到相对坐标
export function normalizeCells(cells: Cell[]): Cell[] {
  const bbox = computeBoundingBox(cells);
  return cells.map(c => ({ col: c.col - bbox.minCol, row: c.row - bbox.minRow }));
}

// 计算两个cells集合是否完全相等（形状+位置）
export function cellsMatch(cellsA: Cell[], cellsB: Cell[]): boolean {
  if (cellsA.length !== cellsB.length) return false;
  const normalizedA = normalizeCells(cellsA);
  const normalizedB = normalizeCells(cellsB);
  for (const a of normalizedA) {
    if (!normalizedB.some(b => cellEq(a, b))) return false;
  }
  return true;
}
